import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const emptyForm = {
  name: '',
  hsnCode: '',
  mrp: '',
  cost: '',
  retailRate: '',
  wholesaleRate: '',
  gst: '18',
  supplierName: '',
  uom: 'PC',
  quantity: '0',
  status: 'available',
  rack: ''
};

const emptyReceive = {
  quantityReceived: '',
  receivedDate: new Date().toISOString().split('T')[0],
  supplier: ''
};

export default function Products() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(''); // 'add' | 'edit' | 'receive'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [receiveForm, setReceiveForm] = useState(emptyReceive);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const fetchProducts = () => {
    setLoading(true);
    API.get('/products')
      .then(r => setProducts(r.data))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModal('add');
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      hsnCode: p.hsnCode || '',
      mrp: p.mrp || '',
      cost: p.cost || '',
      retailRate: p.retailRate || '',
      wholesaleRate: p.wholesaleRate || '',
      gst: p.gst || '18',
      supplierName: p.supplierName || '',
      uom: p.uom || 'PC',
      quantity: p.quantity || '0',
      status: p.status || 'available',
      rack: p.rack !== undefined && p.rack !== null ? String(p.rack) : ''
    });
    setModal('edit');
  };

  const openReceive = (p) => {
    setEditing(p);
    setReceiveForm({
      quantityReceived: '',
      receivedDate: new Date().toISOString().split('T')[0],
      supplier: p.supplierName || ''
    });
    setModal('receive');
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.name || form.mrp === '') return toast.error('Name and MRP are required');
    console.log('Frontend Saving Form:', form);
    setSaving(true);
    try {
      if (modal === 'edit') {
        await API.put(`/products/${editing.id}`, form);
        toast.success('Product updated');
      } else {
        await API.post('/products', form);
        toast.success('Product added');
      }
      fetchProducts();
      setModal('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReceiveStock = async () => {
    if (receiving) return;
    if (!receiveForm.quantityReceived || isNaN(receiveForm.quantityReceived) || parseInt(receiveForm.quantityReceived) <= 0) {
      return toast.error('Please enter a valid quantity received');
    }
    setReceiving(true);
    try {
      await API.post(`/products/${editing.id}/receive-stock`, {
        quantityReceived: parseInt(receiveForm.quantityReceived),
        receivedDate: receiveForm.receivedDate,
        supplier: receiveForm.supplier
      });
      toast.success('Stock updated and logged to Ledger');
      fetchProducts();
      setModal('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update stock');
    } finally {
      setReceiving(false);
    }
  };

  const handleDelete = async (id) => {
    if (deletingId !== null) return;
    setDeletingId(id);
    try {
      await API.delete(`/products/${id}`);
      toast.success('Deleted successfully');
      fetchProducts();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = products.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) || 
    (p.hsnCode && p.hsnCode.includes(search)) ||
    (p.supplierName && p.supplierName.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const activePage = Math.min(currentPage, Math.max(1, totalPages));
  const paginated = filtered.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage);

  return (
    <Layout 
      title="Products & Stock Ledger" 
      actions={isAdmin ? <button className="btn btn-gold" onClick={openAdd}>+ Add Product</button> : null}
    >
      <div className="card">
        <div className="card-header">
          <span className="card-title">All Products ({filtered.length})</span>
          <div className="search-bar" style={{ width: 260 }}>
            <span className="search-icon">🔍</span>
            <input 
              className="form-control" 
              placeholder="Search by name, HSN, supplier..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              style={{ paddingLeft: 36 }} 
            />
          </div>
        </div>
        <div className="table-wrap">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <div className="empty-title">No products found</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>HSN Code</th>
                  <th>Rack</th>
                  <th>MRP</th>
                  {isAdmin && <th>Cost</th>}
                  <th>Retail Rate</th>
                  {isAdmin && <th>Wholesale Rate</th>}
                  <th>GST%</th>
                  <th>UOM</th>
                  <th>Supplier Name</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((p) => (
                  <tr key={p.id}>
                    <td><strong style={{ color: 'var(--navy)' }}>{p.name}</strong></td>
                    <td><span className="badge badge-navy" style={{ textTransform: 'none' }}>{p.hsnCode || '—'}</span></td>
                    <td><span className="badge badge-gray">{p.rack !== undefined && p.rack !== null ? p.rack : '—'}</span></td>
                    <td>₹{p.mrp ? Number(p.mrp).toFixed(2) : '—'}</td>
                    {isAdmin && <td style={{ color: 'var(--gray-500)' }}>₹{p.cost ? Number(p.cost).toFixed(2) : '—'}</td>}
                    <td><strong style={{ color: 'var(--green)' }}>₹{p.retailRate ? Number(p.retailRate).toFixed(2) : '—'}</strong></td>
                    {isAdmin && <td>₹{p.wholesaleRate ? Number(p.wholesaleRate).toFixed(2) : '—'}</td>}
                    <td>{p.gst}%</td>
                    <td><span className="badge badge-gray">{p.uom || 'PC'}</span></td>
                    <td>{p.supplierName || '—'}</td>
                    <td>
                      <span style={{ fontWeight: 600, color: p.quantity < 10 ? 'var(--red)' : 'var(--gray-700)' }}>{p.quantity}</span>
                      {p.quantity < 10 && <span className="badge badge-red" style={{ marginLeft: 6 }}>Low</span>}
                    </td>
                    <td>
                      <span className={`badge ${p.status === 'available' ? 'badge-green' : 'badge-red'}`}>
                        {p.status === 'available' ? '● Available' : '○ Out of Stock'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-success btn-sm" onClick={() => openReceive(p)} disabled={deletingId !== null || saving || receiving}>📥 Receive Stock</button>
                        {isAdmin && (
                          <>
                            <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)} disabled={deletingId !== null || saving || receiving}>✏️ Edit</button>
                            <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(p)} disabled={deletingId !== null || saving || receiving}>
                              {deletingId === p.id ? '⏳' : '🗑️'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid var(--gray-200)', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--gray-600)' }}>
              Showing <strong>{((activePage - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(activePage * itemsPerPage, filtered.length)}</strong> of <strong>{filtered.length}</strong> products
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button 
                className="btn btn-outline btn-sm" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                disabled={activePage === 1}
              >
                ◀ Previous
              </button>
              
              <div style={{ display: 'flex', gap: 4 }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = activePage - 2 + i;
                  if (activePage <= 2) pageNum = i + 1;
                  if (activePage >= totalPages - 1) pageNum = totalPages - 4 + i;
                  
                  if (pageNum < 1 || pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      className={`btn btn-sm ${activePage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                      style={{ minWidth: 32 }}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button 
                className="btn btn-outline btn-sm" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                disabled={activePage === totalPages}
              >
                Next ▶
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal (Admin Only) */}
      {(modal === 'add' || modal === 'edit') && isAdmin && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal('')}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{modal === 'edit' ? 'Edit Product' : 'Add New Product'}</span>
              <button className="modal-close" onClick={() => setModal('')}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product Name *</label>
                <input 
                  className="form-control" 
                  value={form.name} 
                  onChange={e => setForm({ ...form, name: e.target.value })} 
                  placeholder="e.g. 10W BULB" 
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">HSN Code</label>
                  <input 
                    className="form-control" 
                    value={form.hsnCode} 
                    onChange={e => setForm({ ...form, hsnCode: e.target.value })} 
                    placeholder="e.g. 8539" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">UOM</label>
                  <select 
                    className="form-control" 
                    value={form.uom} 
                    onChange={e => setForm({ ...form, uom: e.target.value })}
                  >
                    <option value="PC">PC (Pieces)</option>
                    <option value="MTR">MTR (Meters)</option>
                    <option value="FT">FT (Feet)</option>
                    <option value="KG">KG (Kilograms)</option>
                  </select>
                </div>
              </div>
              <div className="form-row-3">
                <div className="form-group">
                  <label className="form-label">MRP (₹) *</label>
                  <input 
                    className="form-control" 
                    type="number" 
                    min="0" 
                    step="0.01" 
                    value={form.mrp} 
                    onChange={e => setForm({ ...form, mrp: e.target.value })} 
                    placeholder="0.00" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Cost (₹)</label>
                  <input 
                    className="form-control" 
                    type="number" 
                    min="0" 
                    step="0.01" 
                    value={form.cost} 
                    onChange={e => setForm({ ...form, cost: e.target.value })} 
                    placeholder="0.00" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">GST %</label>
                  <select 
                    className="form-control" 
                    value={form.gst} 
                    onChange={e => setForm({ ...form, gst: e.target.value })}
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Retail Rate (₹)</label>
                  <input 
                    className="form-control" 
                    type="number" 
                    min="0" 
                    step="0.01" 
                    value={form.retailRate} 
                    onChange={e => setForm({ ...form, retailRate: e.target.value })} 
                    placeholder="0.00" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Wholesale Rate (₹)</label>
                  <input 
                    className="form-control" 
                    type="number" 
                    min="0" 
                    step="0.01" 
                    value={form.wholesaleRate} 
                    onChange={e => setForm({ ...form, wholesaleRate: e.target.value })} 
                    placeholder="0.00" 
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Supplier Name</label>
                  <input 
                    className="form-control" 
                    value={form.supplierName} 
                    onChange={e => setForm({ ...form, supplierName: e.target.value })} 
                    placeholder="e.g. Philips Lighting" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Rack Number (max 10 chars)</label>
                  <input 
                    className="form-control" 
                    type="text" 
                    maxLength={10}
                    value={form.rack} 
                    onChange={e => {
                      const val = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 10);
                      setForm({ ...form, rack: val });
                    }} 
                    placeholder="e.g. ABC83783" 
                  />
                </div>
              </div>
              {(modal === 'add' || modal === 'edit') && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{modal === 'edit' ? 'Stock Quantity' : 'Initial Stock Quantity'}</label>
                    <input 
                      className="form-control" 
                      type="number" 
                      min="0" 
                      value={form.quantity} 
                      onChange={e => setForm({ ...form, quantity: e.target.value })} 
                      placeholder="0" 
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select 
                      className="form-control" 
                      value={form.status} 
                      onChange={e => setForm({ ...form, status: e.target.value })}
                    >
                      <option value="available">Available</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal('')} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? '⏳ Saving...' : (modal === 'edit' ? 'Update Product' : 'Add Product')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive Stock Modal (Both Admin and Staff) */}
      {modal === 'receive' && editing && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal('')}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">📥 Receive Stock: {editing.name}</span>
              <button className="modal-close" onClick={() => setModal('')}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'var(--gold-pale)', border: '1px solid var(--gold)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 18, fontSize: '0.85rem', color: 'var(--navy)' }}>
                Current Stock Balance: <strong>{editing.quantity} {editing.uom || 'PC'}</strong>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantity Received *</label>
                  <input 
                    className="form-control" 
                    type="number" 
                    min="1" 
                    value={receiveForm.quantityReceived} 
                    onChange={e => setReceiveForm({ ...receiveForm, quantityReceived: e.target.value })} 
                    placeholder="e.g. 50" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Received Date</label>
                  <input 
                    className="form-control" 
                    type="date" 
                    value={receiveForm.receivedDate} 
                    onChange={e => setReceiveForm({ ...receiveForm, receivedDate: e.target.value })} 
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Supplier / Brand</label>
                <input 
                  className="form-control" 
                  value={receiveForm.supplier} 
                  onChange={e => setReceiveForm({ ...receiveForm, supplier: e.target.value })} 
                  placeholder="e.g. Philips Lighting" 
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal('')} disabled={receiving}>Cancel</button>
              <button className="btn btn-gold" onClick={handleReceiveStock} disabled={receiving}>
                {receiving ? '⏳ Recording...' : 'Record Stock Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">⚠️</div>
            <div className="confirm-title">Delete Product?</div>
            <div className="confirm-desc">
              Are you sure you want to permanently delete <strong>{confirmDelete.name}</strong>?
            </div>
            <div className="confirm-buttons">
              <button className="btn btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => {
                const id = confirmDelete.id;
                setConfirmDelete(null);
                handleDelete(id);
              }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
